import { Suspense } from "react";
import * as Sentry from "@sentry/react";
import { createBrowserRouter, Navigate } from "react-router-dom";

import { UserDepartment, UserRoleEnum } from "@/api/user/models";
import { LoadingSpinner } from "@/components/common";
import {
  COLLABORATORS_MANAGEMENT_ROLES,
  COMPOSITE_ACCESS_ALLOWED_ROLES,
  REJECTED_CANDIDATES_ALLOWED_ROLES,
  TALENT_EDIT_ALLOWED_ROLES,
} from "@/constants/userPermissions";
import { CreateCompositePage } from "@/pages/casting/composites/create";
import { EditCompositePage } from "@/pages/casting/composites/edit";
import {
  ConditionalTalentBasePage,
  ConditionalTalentBookPage,
  ConditionalTalentDataPage,
  ConditionalTalentDetailsPage,
  ConditionalTalentsPage,
} from "./ConditionalPages";
import { DepartmentGuard } from "./DepartmentGuard";
import { DepartmentRedirect } from "./DepartmentRedirect";
import {
  ApprovedStagePage,
  ArchivedTalentsPage,
  CompositesPage,
  CollaboratorsPage,
  EditedStagePage,
  JobCastingTalentsListPage,
  JobDetailsPage,
  JobsPage,
  EditJobPage,
  LoginPage,
  MyAccountPage,
  NewJobPage,
  PreSelectionStagePage,
  ProductionRejectedPage,
  RejectedTalentDataPage,
  ProtectedLayout,
  PublicContractPreviewPage,
  PublicLayout,
  PublicScheduleRegistrationPage,
  PublicTalentUpdatePage,
  RootLayout,
  ScheduleArtisticProductionPage,
  ScheduleContractsPage,
  SchedulePhotographyPage,
  ScheduleProductionPage,
  ScheduleTalentDataPage,
  StageLayout,
  TableTestPage,
  TalentContractsPage,
  TalentCreatePage,
  TalentDataEditPage,
  TalentDocumentsPage,
  TalentJobsPage,
} from "./LazyPages";
import { JobStagesEnum, JobStagesTranslateToSlug } from "@/enums/job";
import { ProtectedRoute } from "./ProtectedRoute";
import { PublicRoute } from "./PublicRoute";
import { RoleGuard } from "./RoleGuard";
import { RootRedirect } from "./RootRedirect";
import { SchedulePage } from "@/pages/schedule";
import { ScheduleDetailLayout } from "@/layouts/ScheduleDetailLayout";

const SuspenseWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingSpinner />}>{children}</Suspense>
);

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

export const router = sentryCreateBrowserRouter([
  {
    path: "/",
    element: (
      <SuspenseWrapper>
        <RootLayout />
      </SuspenseWrapper>
    ),
    children: [
      {
        index: true,
        element: <RootRedirect />,
      },
      {
        path: "ficha-cadastral-agendamento",
        element: (
          <SuspenseWrapper>
            <PublicLayout />
          </SuspenseWrapper>
        ),
        children: [
          {
            index: true,
            element: <PublicScheduleRegistrationPage />,
          },
        ],
      },
      {
        path: "/",
        element: (
          <PublicRoute>
            <SuspenseWrapper>
              <PublicLayout />
            </SuspenseWrapper>
          </PublicRoute>
        ),
        children: [
          {
            path: "login",
            element: (
              <SuspenseWrapper>
                <LoginPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "atualizar-dados",
            element: (
              <SuspenseWrapper>
                <PublicTalentUpdatePage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "dev/table-test",
            element: (
              <SuspenseWrapper>
                <TableTestPage />
              </SuspenseWrapper>
            ),
          },
        ],
      },
      {
        path: ":department",
        element: (
          <ProtectedRoute>
            <SuspenseWrapper>
              <ProtectedLayout />
            </SuspenseWrapper>
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <DepartmentRedirect />,
          },
          {
            path: "colaboradores",
            element: (
              <RoleGuard allowedRoles={COLLABORATORS_MANAGEMENT_ROLES}>
                <SuspenseWrapper>
                  <CollaboratorsPage />
                </SuspenseWrapper>
              </RoleGuard>
            ),
          },
          {
            path: "minha-conta",
            element: (
              <SuspenseWrapper>
                <MyAccountPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "agenda",
            element: (
              <DepartmentGuard allowedDepartments={[UserDepartment.REBOUCAS, UserDepartment.FRADIQUE]}>
                <SuspenseWrapper>
                  <SchedulePage />
                </SuspenseWrapper>
              </DepartmentGuard>
            ),
          },
          {
            path: "agenda/:scheduleId",
            element: (
              <DepartmentGuard allowedDepartments={[UserDepartment.REBOUCAS, UserDepartment.FRADIQUE]}>
                <SuspenseWrapper>
                  <ScheduleDetailLayout />
                </SuspenseWrapper>
              </DepartmentGuard>
            ),
            children: [
              {
                index: true,
                element: <Navigate to="dados-talento" replace />,
              },
              {
                path: "dados-talento",
                element: <ScheduleTalentDataPage />,
              },
              {
                path: "producao",
                element: <ScheduleProductionPage />,
              },
              {
                path: "contratos",
                element: <ScheduleContractsPage />,
              },
              {
                path: "producao-artistica",
                element: <ScheduleArtisticProductionPage />,
              },
              {
                path: "fotografia",
                element: <SchedulePhotographyPage />,
              },
            ],
          },
          {
            path: "reprovados",
            element: (
              <DepartmentGuard allowedDepartments={[UserDepartment.REBOUCAS, UserDepartment.FRADIQUE]}>
                <RoleGuard allowedRoles={REJECTED_CANDIDATES_ALLOWED_ROLES}>
                  <SuspenseWrapper>
                    <ProductionRejectedPage />
                  </SuspenseWrapper>
                </RoleGuard>
              </DepartmentGuard>
            ),
          },
          {
            path: "reprovados/:talentId/dados-talento",
            element: (
              <DepartmentGuard allowedDepartments={[UserDepartment.REBOUCAS, UserDepartment.FRADIQUE]}>
                <RoleGuard allowedRoles={[UserRoleEnum.ADMINISTRATOR, UserRoleEnum.COORDINATOR, UserRoleEnum.PRODUCER]}>
                  <SuspenseWrapper>
                    <RejectedTalentDataPage />
                  </SuspenseWrapper>
                </RoleGuard>
              </DepartmentGuard>
            ),
          },
          {
            path: "arquivados",
            element: (
              <DepartmentGuard allowedDepartments={[UserDepartment.CASTING]}>
                <SuspenseWrapper>
                  <ArchivedTalentsPage />
                </SuspenseWrapper>
              </DepartmentGuard>
            ),
          },
          {
            path: "jobs",
            children: [
              {
                index: true,
                element: (
                  <DepartmentGuard allowedDepartments={[UserDepartment.CASTING]}>
                    <SuspenseWrapper>
                      <JobsPage />
                    </SuspenseWrapper>
                  </DepartmentGuard>
                ),
              },
              {
                path: "novo-job",
                element: (
                  <DepartmentGuard allowedDepartments={[UserDepartment.CASTING]}>
                    <SuspenseWrapper>
                      <NewJobPage />
                    </SuspenseWrapper>
                  </DepartmentGuard>
                ),
              },
              {
                path: ":id/:stageId/editar-job",
                element: (
                  <DepartmentGuard allowedDepartments={[UserDepartment.CASTING]}>
                    <SuspenseWrapper>
                      <EditJobPage />
                    </SuspenseWrapper>
                  </DepartmentGuard>
                ),
              },
              {
                path: ":id",
                element: (
                  <DepartmentGuard allowedDepartments={[UserDepartment.CASTING]}>
                    <SuspenseWrapper>
                      <StageLayout />
                    </SuspenseWrapper>
                  </DepartmentGuard>
                ),
                children: [
                  {
                    path: JobStagesTranslateToSlug.CASTING,
                    handle: { stage: JobStagesEnum.CASTING },
                    element: (
                      <SuspenseWrapper>
                        <JobDetailsPage />
                      </SuspenseWrapper>
                    ),
                  },
                  {
                    path: JobStagesTranslateToSlug.PRE_SELECTION,
                    handle: { stage: JobStagesEnum.PRE_SELECTION },
                    element: (
                      <SuspenseWrapper>
                        <PreSelectionStagePage />
                      </SuspenseWrapper>
                    ),
                  },
                  {
                    path: JobStagesTranslateToSlug.EDITED,
                    handle: { stage: JobStagesEnum.EDITED },
                    element: (
                      <SuspenseWrapper>
                        <EditedStagePage />
                      </SuspenseWrapper>
                    ),
                  },
                  {
                    path: JobStagesTranslateToSlug.APPROVAL,
                    handle: { stage: JobStagesEnum.APPROVED },
                    element: (
                      <SuspenseWrapper>
                        <ApprovedStagePage />
                      </SuspenseWrapper>
                    ),
                  },
                ],
              },
              {
                path: ":id/casting/talentos-selecionados",
                element: (
                  <DepartmentGuard allowedDepartments={[UserDepartment.CASTING]}>
                    <SuspenseWrapper>
                      <JobCastingTalentsListPage />
                    </SuspenseWrapper>
                  </DepartmentGuard>
                ),
              },
            ],
          },
          {
            path: "talentos",
            children: [
              {
                index: true,
                element: (
                  <DepartmentGuard allowedDepartments={[UserDepartment.CASTING, UserDepartment.PHOTO_LAB]}>
                    <SuspenseWrapper>
                      <ConditionalTalentsPage />
                    </SuspenseWrapper>
                  </DepartmentGuard>
                ),
              },
              {
                path: "cadastrar",
                element: (
                  <DepartmentGuard allowedDepartments={[UserDepartment.CASTING]}>
                    <SuspenseWrapper>
                      <TalentCreatePage />
                    </SuspenseWrapper>
                  </DepartmentGuard>
                ),
              },
              {
                path: ":id",
                element: (
                  <SuspenseWrapper>
                    <ConditionalTalentBasePage />
                  </SuspenseWrapper>
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <SuspenseWrapper>
                        <ConditionalTalentDetailsPage />
                      </SuspenseWrapper>
                    ),
                  },
                  {
                    path: "dados-talento",
                    children: [
                      {
                        index: true,
                        element: (
                          <DepartmentGuard allowedDepartments={[UserDepartment.CASTING, UserDepartment.PHOTO_LAB]}>
                            <SuspenseWrapper>
                              <ConditionalTalentDataPage />
                            </SuspenseWrapper>
                          </DepartmentGuard>
                        ),
                      },
                      {
                        path: "editar",
                        element: (
                          <DepartmentGuard allowedDepartments={[UserDepartment.CASTING]}>
                            <RoleGuard allowedRoles={TALENT_EDIT_ALLOWED_ROLES}>
                              <SuspenseWrapper>
                                <TalentDataEditPage />
                              </SuspenseWrapper>
                            </RoleGuard>
                          </DepartmentGuard>
                        ),
                      },
                    ],
                  },
                  {
                    path: "book",
                    element: (
                      <DepartmentGuard allowedDepartments={[UserDepartment.CASTING, UserDepartment.PHOTO_LAB]}>
                        <SuspenseWrapper>
                          <ConditionalTalentBookPage />
                        </SuspenseWrapper>
                      </DepartmentGuard>
                    ),
                  },
                  {
                    path: "documentos",
                    element: (
                      <DepartmentGuard allowedDepartments={[UserDepartment.CASTING]}>
                        <SuspenseWrapper>
                          <TalentDocumentsPage />
                        </SuspenseWrapper>
                      </DepartmentGuard>
                    ),
                  },
                  {
                    path: "composites",
                    children: [
                      {
                        index: true,
                        element: (
                          <DepartmentGuard allowedDepartments={[UserDepartment.CASTING]}>
                            <RoleGuard allowedRoles={COMPOSITE_ACCESS_ALLOWED_ROLES}>
                              <SuspenseWrapper>
                                <CompositesPage />
                              </SuspenseWrapper>
                            </RoleGuard>
                          </DepartmentGuard>
                        ),
                      },
                      {
                        path: "criar",
                        element: (
                          <DepartmentGuard allowedDepartments={[UserDepartment.CASTING]}>
                            <RoleGuard allowedRoles={COMPOSITE_ACCESS_ALLOWED_ROLES}>
                              <SuspenseWrapper>
                                <CreateCompositePage />
                              </SuspenseWrapper>
                            </RoleGuard>
                          </DepartmentGuard>
                        ),
                      },
                      {
                        path: "editar/:compositeId",
                        element: (
                          <DepartmentGuard allowedDepartments={[UserDepartment.CASTING]}>
                            <RoleGuard allowedRoles={COMPOSITE_ACCESS_ALLOWED_ROLES}>
                              <SuspenseWrapper>
                                <EditCompositePage />
                              </SuspenseWrapper>
                            </RoleGuard>
                          </DepartmentGuard>
                        ),
                      },
                    ],
                  },
                  {
                    path: "contratos",
                    element: (
                      <DepartmentGuard
                        allowedDepartments={[UserDepartment.CASTING, UserDepartment.REBOUCAS, UserDepartment.FRADIQUE]}
                      >
                        <SuspenseWrapper>
                          <TalentContractsPage />
                        </SuspenseWrapper>
                      </DepartmentGuard>
                    ),
                  },
                  {
                    path: "historico-jobs",
                    element: (
                      <DepartmentGuard allowedDepartments={[UserDepartment.CASTING]}>
                        <SuspenseWrapper>
                          <TalentJobsPage />
                        </SuspenseWrapper>
                      </DepartmentGuard>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/contratos/preview/:previewToken",
    element: (
      <SuspenseWrapper>
        <PublicContractPreviewPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "*",
    element: <Navigate to="/casting/talentos" replace />,
  },
]);
